import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Wrench, Building } from "lucide-react";
import { ChatRoomMessageMetaData } from "@/models/chat.model";
import { useNavigate } from "react-router";
import { format } from "date-fns";

interface Props {
  metaData: ChatRoomMessageMetaData;
}

export default function RichMessage({ metaData }: Props) {
  const navigate = useNavigate();

  // Type guard to check if this is a system message with appointment link
  const isSystemMessageWithLink = (
    data: any
  ): data is {
    systemMessageType: string;
    appointmentId: string;
    appointmentLink?: {
      appointmentId: string;
      propertyName: string;
      unitNumber: string;
      date: string;
      serviceName: string;
      vendorName: string;
    };
  } => {
    return data && typeof data.systemMessageType === "string" && data.appointmentLink;
  };

  if (!isSystemMessageWithLink(metaData)) {
    return null;
  }

  const { appointmentLink } = metaData;
  if (!appointmentLink) return null;

  const handleViewAppointment = () => {
    navigate(`/appointments/${appointmentLink.appointmentId}`);
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "MMM dd, yyyy");
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="mt-2 border border-border bg-card/50">
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2 flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-medium text-sm truncate">
                {appointmentLink.propertyName} {appointmentLink.unitNumber}
              </span>
            </div>

            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1">
                <Wrench className="h-3 w-3" />
                {appointmentLink.serviceName}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(appointmentLink.date)}
              </div>
            </div>
          </div>

          <Button size="sm" variant="outline" onClick={handleViewAppointment} className="shrink-0">
            View
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
